import type { User } from '@talos/kyoko';
import { MixpanelEventSource, type ACTION } from '@talos/kyoko';
import type { FeatureFlags } from '../../../../hooks/useFeatureFlag';
import { isUserAuthorized } from '../../../../hooks/useRoleAuth';
import {
  OPERATIONS_NAV,
  PORTFOLIO_LITE_NAV,
  PORTFOLIO_NAV,
  type NavigationItem,
} from '../../../../tokens/navigationPaths';
import type { LayoutOptionTabProps, LayoutOptionView } from '../portfolioDashboard.types';
import type { PortfolioViewType } from '../portfolioDashboardLayouts';
import { getOperationsLayout } from './dockviewWorkspaceConfigs/operations/operations.defaultLayout';
import { getPortfolioManagementLayout } from './dockviewWorkspaceConfigs/portfolioManagement/portfolioManagement.defaultLayout';

type FlexibleLayoutProps = Extract<LayoutOptionTabProps, { layoutType: 'flexible' }>;
type FixedLayoutProps = Extract<LayoutOptionTabProps, { layoutType: 'fixed' }>;

type FlexibleLayoutConfig = Pick<
  FlexibleLayoutProps,
  'layoutType' | 'getDefaultLayout' | 'availablePanels' | 'activeFilters' | 'mixpanelEventSource'
>;
type FixedLayoutConfig = Pick<
  FixedLayoutProps,
  'layoutType' | 'content' | 'activeFilters' | 'activeControls' | 'mixpanelEventSource'
>;
// Layout-specific configuration for each tab (keyed by route)
// This contains all the layout-specific properties that are added to the base navigation structure
const LAYOUT_CONFIG: Record<`/${string}`, FlexibleLayoutConfig | FixedLayoutConfig> = {
  '/portfolio-management/overview': {
    layoutType: 'fixed',
    content: 'PM-Overview',
    activeFilters: ['SubAccounts'],
    activeControls: {
      showRollupHierarchy: true,
      menuOptions: ['includeCash', 'showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementOverview,
  },
  '/portfolio-management/performance': {
    layoutType: 'fixed',
    content: 'PM-Performance',
    activeFilters: ['SubAccounts'],
    activeControls: {
      showRollupHierarchy: true,
      menuOptions: ['includeCash', 'showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementPerformance,
  },
  '/portfolio-management/margin': {
    layoutType: 'fixed',
    content: 'PM-Margin',
    activeFilters: ['SubAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementMargin,
  },
  '/portfolio-management/transactions': {
    layoutType: 'flexible',
    getDefaultLayout: () => getPortfolioManagementLayout(),
    availablePanels: 'pm-transactions',
    activeFilters: ['SubAccounts'],
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementTransactions,
  },
  '/portfolio-management/reports': {
    layoutType: 'fixed',
    content: 'PM-Reports',
    activeFilters: ['SubAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioManagementReports,
  },
  '/risk-management/exposures': {
    layoutType: 'fixed',
    content: 'Risk-Exposures',
    activeFilters: ['SubAccounts'],
    activeControls: {
      showRollupHierarchy: true,
      menuOptions: ['includeCash', 'showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioRiskExposures,
  },
  '/operations/overview': {
    layoutType: 'fixed',
    content: 'Operations-Overview',
    activeFilters: ['MarketAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsOverview,
  },
  '/operations/transactions': {
    layoutType: 'flexible',
    getDefaultLayout: () => getOperationsLayout(),
    availablePanels: 'operations-transactions',
    activeFilters: ['MarketAccounts'],
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsTransactions,
  },
  '/operations/lp-settlement-legacy': {
    layoutType: 'fixed',
    content: 'Operations-Settlement',
    activeFilters: [],
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsSettlement,
  },
  '/operations/lp-settlement': {
    layoutType: 'fixed',
    content: 'LP-Settlement',
    activeFilters: [],
    mixpanelEventSource: MixpanelEventSource.PortfolioLpSettlement,
  },
  '/operations/reconciliation': {
    layoutType: 'fixed',
    content: 'Operations-Reconciliation',
    activeFilters: [],
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsReconciliation,
  },
  '/operations/reports': {
    layoutType: 'fixed',
    content: 'Operations-Reports',
    activeFilters: ['MarketAccounts'],
    activeControls: {
      menuOptions: ['showZeroBalances'],
    },
    mixpanelEventSource: MixpanelEventSource.PortfolioOperationsReports,
  },
};

LAYOUT_CONFIG['/positions/overview'] = LAYOUT_CONFIG['/portfolio-management/overview'];
LAYOUT_CONFIG['/positions/performance'] = LAYOUT_CONFIG['/portfolio-management/performance'];
LAYOUT_CONFIG['/positions/margin'] = LAYOUT_CONFIG['/portfolio-management/margin'];
LAYOUT_CONFIG['/positions/transactions'] = LAYOUT_CONFIG['/portfolio-management/transactions'];
LAYOUT_CONFIG['/positions/reports'] = LAYOUT_CONFIG['/portfolio-management/reports'];
LAYOUT_CONFIG['/risk/exposures'] = LAYOUT_CONFIG['/risk-management/exposures'];
LAYOUT_CONFIG['/treasury-management/overview'] = LAYOUT_CONFIG['/operations/overview'];
LAYOUT_CONFIG['/treasury-management/transactions'] = LAYOUT_CONFIG['/operations/transactions'];
LAYOUT_CONFIG['/treasury-management/lp-settlement-legacy'] = LAYOUT_CONFIG['/operations/lp-settlement-legacy'];
LAYOUT_CONFIG['/treasury-management/lp-settlement'] = LAYOUT_CONFIG['/operations/lp-settlement'];
LAYOUT_CONFIG['/treasury-management/operational-reports'] = LAYOUT_CONFIG['/operations/reports'];
LAYOUT_CONFIG['/operational-risk/reconciliation'] = LAYOUT_CONFIG['/operations/reconciliation'];

/**
 * Builds layout options from base navigation structure and layout configuration
 */
function buildLayoutOptions(
  navigationItem: NavigationItem,
  pathPrefix: '/portfolio' | '/operations' = '/portfolio'
): LayoutOptionView<PortfolioViewType>[] {
  const result: LayoutOptionView<PortfolioViewType>[] = [];

  // Iterate through sections (Portfolio Management, Risk Management, Operations)
  for (const section of navigationItem.children || []) {
    if (!section.path) {
      continue;
    }
    const sectionValue = section.path.split('/').pop() as PortfolioViewType;

    const layoutSection: LayoutOptionView<PortfolioViewType> = {
      label: section.label || '',
      route: section.path.replace(new RegExp(`^${pathPrefix}/`), '/') as `/${string}`,
      value: sectionValue,
      tabs: [],
    };

    // Iterate through tabs within each section
    for (const tab of section.children || []) {
      if (!tab.path) {
        continue;
      }
      // Strip /portfolio prefix to get the lookup key (e.g., '/operations/overview' from '/portfolio/operations/overview')
      const lookupKey = tab.path.replace(new RegExp(`^${pathPrefix}/`), '/') as `/${string}`;
      const layoutConfig = LAYOUT_CONFIG[lookupKey];

      if (!layoutConfig) {
        continue;
      }

      // Extract just the relative route (e.g., '/overview' from '/portfolio/operations/overview')
      const relativeRoute = tab.path.substring(tab.path.lastIndexOf('/')) as `/${string}`;

      const tabConfig: LayoutOptionView<PortfolioViewType>['tabs'][number] = {
        label: tab.label || '',
        route: relativeRoute,
        ...layoutConfig,
      } as LayoutOptionView<PortfolioViewType>['tabs'][number];

      // Convert NavigationItem visibility to LayoutOptionTab visibility, including the parent section.
      if (section.isVisible || tab.isVisible) {
        tabConfig.isVisibleIf = (flags: FeatureFlags, user: User) => {
          const isAuthorized = (permission: ACTION) => isUserAuthorized(user, permission, false);
          if (section.isVisible && !section.isVisible(flags, isAuthorized)) {
            return false;
          }
          if (tab.isVisible && !tab.isVisible(flags, isAuthorized)) {
            return false;
          }

          return true;
        };
      }

      layoutSection.tabs.push(tabConfig);
    }

    // Only add sections that have tabs
    if (layoutSection.tabs.length > 0) {
      result.push(layoutSection);
    }
  }

  return result;
}

function remapNavigationPaths(item: NavigationItem, from: string, to: string): NavigationItem {
  return {
    ...item,
    path: item.path?.replace(from, to),
    children: (item.children as NavigationItem[] | undefined)?.map(child => remapNavigationPaths(child, from, to)),
  } as NavigationItem;
}

function getLegacyFullLayoutOptions(): LayoutOptionView<PortfolioViewType>[] {
  const fullChildren = PORTFOLIO_NAV.children as NavigationItem[] | undefined;
  const positions = fullChildren?.find(item => item.path === '/portfolio/positions');
  const risk = fullChildren?.find(item => item.path === '/portfolio/risk');
  const liteOperations = PORTFOLIO_LITE_NAV.find(item => item.path === '/portfolio/operations');

  if (!positions || !risk || !liteOperations) {
    return [];
  }

  const operationsChildren: NavigationItem[] = [
    {
      path: '/portfolio/operations/overview',
      label: 'Overview',
    },
    ...((liteOperations.children as NavigationItem[] | undefined) ?? []).map(item => {
      if (item.path === '/portfolio/operations/reconciliation') {
        return { ...item, isVisible: undefined };
      }
      return item;
    }),
  ];

  return buildLayoutOptions({
    path: '/portfolio',
    label: 'Portfolio',
    children: [
      remapNavigationPaths(positions, '/portfolio/positions', '/portfolio/portfolio-management'),
      remapNavigationPaths(risk, '/portfolio/risk', '/portfolio/risk-management'),
      { ...liteOperations, children: operationsChildren } as NavigationItem,
    ],
  });
}

export function getPortfolioLayoutOptions(): LayoutOptionView<PortfolioViewType>[] {
  return [...buildLayoutOptions(PORTFOLIO_NAV), ...getLegacyFullLayoutOptions()];
}

export function getPortfolioLiteLayoutOptions(): LayoutOptionView<PortfolioViewType>[] {
  const portfolioNav = { ...PORTFOLIO_NAV };
  portfolioNav.children = PORTFOLIO_LITE_NAV;
  return buildLayoutOptions(portfolioNav);
}

export function getOperationsLayoutOptions(): LayoutOptionView<PortfolioViewType>[] {
  return buildLayoutOptions(OPERATIONS_NAV, '/operations');
}
